import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ChatThread } from "@shared/schema";
import { chatThreadsService } from "@/services/chatThreadsService";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";

export type ThreadSummary = Omit<ChatThread, "messages"> & { messageCount: number };

interface ChatThreadsContextValue {
  threads: ThreadSummary[];
  isLoading: boolean;
  isFetching: boolean;
  refresh: () => void;
  enable: () => void;
  disable: () => void;
}

const ChatThreadsContext = createContext<ChatThreadsContextValue | undefined>(undefined);

export function ChatThreadsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { currentWorkspaceId } = useWorkspace();
  const queryClient = useQueryClient();
  const [enabledCount, setEnabledCount] = useState(0);

  const enable = useCallback(() => {
    setEnabledCount((count) => count + 1);
  }, []);

  const disable = useCallback(() => {
    setEnabledCount((count) => Math.max(0, count - 1));
  }, []);

  useEffect(() => {
    setEnabledCount(0);
    queryClient.removeQueries({ queryKey: ["chat-thread-summaries"] });
  }, [queryClient, user?.id]);

  const queryKey = useMemo(
    () => ["chat-thread-summaries", user?.id, currentWorkspaceId ?? "personal"],
    [user?.id, currentWorkspaceId]
  );

  const query = useQuery<ThreadSummary[]>({
    queryKey,
    queryFn: async () => {
      if (!user) return [];
      return chatThreadsService.getThreadSummaries(currentWorkspaceId ?? null);
    },
    enabled: !!user?.id && enabledCount > 0,
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: false,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey });
  };

  return (
    <ChatThreadsContext.Provider
      value={{
        threads: query.data ?? [],
        isLoading: query.isLoading,
        isFetching: query.isFetching,
        refresh,
        enable,
        disable,
      }}
    >
      {children}
    </ChatThreadsContext.Provider>
  );
}

export function useThreadSummaries(options?: { enabled?: boolean }) {
  const context = useContext(ChatThreadsContext);
  const enabled = options?.enabled ?? true;

  const { user } = useAuth();
  const { currentWorkspaceId } = useWorkspace();
  const queryClient = useQueryClient();
  const fallbackQuery = useQuery<ThreadSummary[]>({
    queryKey: ["chat-thread-summaries", user?.id, currentWorkspaceId ?? "personal"],
    queryFn: async () => {
      if (!user) return [];
      return chatThreadsService.getThreadSummaries(currentWorkspaceId ?? null);
    },
    enabled: !context && !!user?.id && enabled,
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!context || !enabled) return;
    context.enable();
    return () => context.disable();
  }, [context, enabled]);

  if (!context) {
    return {
      threads: fallbackQuery.data ?? [],
      isLoading: fallbackQuery.isLoading,
      isFetching: fallbackQuery.isFetching,
      refresh: () =>
        queryClient.invalidateQueries({
          queryKey: ["chat-thread-summaries", user?.id, currentWorkspaceId ?? "personal"],
        }),
      enable: () => undefined,
      disable: () => undefined,
    };
  }

  const { enable, disable, ...rest } = context;
  return { ...rest, enable, disable };
}
